"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Checkbox,
  Panel,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { CheckList } from "@/components/tools/research-writing/check-list";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  buildPeerReviewReport,
  emptyMajor,
  MAX_MAJOR,
  type MajorComment,
  type PeerReviewReportInput,
  type Priority,
  type Recommendation,
  reportChecks,
  reportCounts,
} from "@/lib/tool-kit/research-writing/peer-review-report";

/** English strings. A translated content file overrides these through `ui`. */
export const PEER_REVIEW_REPORT_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  manuscriptHeading: "The manuscript",
  titleLabel: "Manuscript title",
  titlePlaceholder: "Paste the title as it appears on the submission",
  journalLabel: "Journal (optional)",
  journalPlaceholder: "Educational Research Letters",
  idLabel: "Manuscript ID (optional)",
  idPlaceholder: "ERL-2026-0412",
  summaryHeading: "Summary and strengths",
  summaryLabel: "Summary of the submission, in your own words",
  summaryPlaceholder: "This manuscript reports … The authors conclude that …",
  summaryHint:
    "Shows the editor you understood the work. Describe what the authors claim, not what you think of it.",
  strengthsLabel: "Strengths",
  strengthsPlaceholder: "What the work does well, specifically",
  majorHeading: "Major comments",
  majorNote:
    "Problems that bear on whether the conclusions hold. Say where, what, and what would resolve it — and whether it is essential.",
  majorCardLabel: "Major comment {n}",
  locationLabel: "Where (section, page, lines)",
  locationPlaceholder: "Methods, p. 6, ll. 98–104",
  concernLabel: "The concern",
  concernPlaceholder: "What the problem is and why it affects the conclusions",
  requestLabel: "What would resolve it",
  requestPlaceholder: "The analysis, evidence or change you are asking for",
  priorityLabel: "How important is the request?",
  priorities: {
    "": "Not stated",
    essential: "Essential to support the conclusions",
    strengthen: "Would strengthen or extend the work",
  },
  addMajor: "Add a major comment",
  removeMajor: "Remove major comment {n}",
  minorHeading: "Minor comments, scope and recommendation",
  minorLabel: "Minor comments — one per line",
  minorPlaceholder:
    "Table 2: give the units for attendance\np. 8, l. 140: separate statistical and practical significance",
  scopeLabel: "Parts you could not assess (optional)",
  scopePlaceholder:
    "I did not assess the qualitative interviews in the supplement.",
  recommendationLabel: "Recommendation",
  recommendationOptions: {
    "": "Choose a recommendation",
    accept: "Accept",
    minor: "Minor revision",
    major: "Major revision",
    reject: "Reject",
  },
  rationaleLabel: "Reason for your recommendation",
  rationalePlaceholder:
    "One or two sentences that connect the recommendation to your comments",
  editorHeading: "For the editor, and your declarations",
  confidentialLabel: "Confidential comments to the editor",
  confidentialPlaceholder:
    "Ethical concerns, suspected overlap with other work, or anything the editor needs to know that the authors should not see",
  confidentialHint:
    "COPE: most feedback belongs in the report for the authors, and this space is not for criticism made because the authors will not see it.",
  noConflictLabel: "I have no competing interests to declare",
  keptConfidentialLabel:
    "I have kept the manuscript and my review confidential",
  helpersLabel: "Anyone who helped you review (optional)",
  helpersPlaceholder: "Names, one per line — journals ask you to credit them",
  resultsHeading: "Report check",
  statWords: "Words for the authors",
  statMajor: "Major comments",
  statMinor: "Minor comments",
  statEssential: "Marked essential",
  checksLabel: "Checklist",
  checkStates: { done: "Done", todo: "To do", note: "Check" },
  checkText: {
    summary_done: "The summary is in.",
    summary_todo: "Write a summary of the submission in your own words.",
    strengths_done: "Strengths are named.",
    strengths_note:
      "No strengths yet. Editors ask for an honest and fair view of strengths and weaknesses.",
    specific_done: "Every major comment says where it applies.",
    specific_note:
      "Major comment {items} has no location. A specific criticism names the section, page or lines.",
    priority_done: "Every request is marked essential or not.",
    priority_note:
      "Major comment {items} makes a request without saying whether it is essential. COPE asks reviewers to make that clear.",
    consistent_none: "Choose a recommendation.",
    consistent_done: "The recommendation is consistent with your comments.",
    consistent_acceptMajor:
      "You recommend acceptance but list major comments. Reconcile them before sending.",
    consistent_minorEssential:
      "You recommend minor revision but marked a request essential. Check whether that is a major revision.",
    consistent_revisionNoMajor:
      "You recommend revision or rejection but list no major comment. Say what the decision rests on.",
    tone_done: "No loaded words found in what the authors will read.",
    tone_note:
      "Check the wording of: {items}. COPE asks reviewers to avoid hostile or derogatory language. This is a short word list, not a judgement of your tone.",
    declarations_done: "Declarations are ticked.",
    declarations_todo:
      "Tick the declarations your journal asks for, or state any competing interest.",
  },
  fileName: "peer-review-report",
  document: {
    heading: "Your review report",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part has your content.",
  },
  defaultTitle: "Referee Report",
  titleLead: "Referee report:",
  subtitle: "Review report template",
  subtitleJournal: "{journal}",
  subtitleId: "Manuscript {id}",
  headings: {
    summary: "Summary of the submission",
    strengths: "Strengths",
    major: "Major comments",
    minor: "Minor comments",
    scope: "Scope of this review",
    recommendation: "Recommendation",
    editor: "Confidential comments to the editor",
    declarations: "Declarations",
  },
  tips: {
    authors:
      "Everything down to the recommendation is written for the authors to read. Follow your journal’s form where it has one.",
    summary:
      "In your own words: the question, the methods and the main conclusions. No judgement yet.",
    major:
      "Number each comment. Be specific, give evidence for general statements, and say which requests are essential.",
    minor:
      "Clarity, labels, typos and small omissions that do not change the conclusions.",
    recommendation:
      "Keep it consistent with your comments. The editor makes the final decision.",
    editor:
      "Only the editor sees this. Most feedback belongs above, where the authors can answer it.",
  },
  prompts: {
    summary:
      "Summarize the submission in a short paragraph: “This manuscript reports a [design] study of [question] in [sample]. The authors conclude that [claim].”",
    strengths:
      "Name what works, specifically: the question, the design, the data, the clarity.",
    major:
      "For each major comment: “[Section, page or lines]: [the problem]. This matters because [effect on the conclusions]. [What would resolve it].”",
    minor:
      "One short comment per line, with its location: “p. 4, l. 12: [typo, definition or unclear sentence].”",
    scope:
      "If you could assess only part of the manuscript, say which parts, and why.",
    recommendation:
      "Choose accept, minor revision, major revision or reject, in the form your journal offers.",
    rationale:
      "Give the reason: “My recommendation rests mainly on [comment numbers], because [reason].”",
    editor:
      "Add anything only the editor should see: ethical concerns, suspected overlap with other work, or undeclared interests. Leave it empty if there is none.",
    conflict:
      "Declare any competing interest — personal, financial, intellectual, professional, political or religious — or ask the journal if you are unsure.",
    helpers:
      "Name anyone who helped you review, or leave this out if no one did.",
  },
  majorLabel: "Major comment {n}.",
  majorPrompt: "[Describe the concern.]",
  requestLead: "What would resolve it:",
  priorityLines: {
    essential: "This is essential to support the conclusions.",
    strengthen: "This would strengthen the work but is not essential.",
  },
  recommendationLine: "My recommendation:",
  recommendations: {
    accept: "Accept.",
    minor: "Minor revision.",
    major: "Major revision.",
    reject: "Reject.",
  },
  declarations: {
    noConflict: "I have no competing interests to declare.",
    keptConfidential:
      "I have kept the manuscript and this review confidential.",
    helpers: "Others who helped with this review:",
  },
  example: {
    title:
      "Peer tutoring and reading attainment in secondary schools: a cluster randomised trial",
    journal: "Educational Research Letters",
    manuscriptId: "ERL-2026-0412",
    summary:
      "This manuscript reports a cluster randomised trial of a peer-tutoring programme in 14 secondary schools. The authors conclude that the programme raised reading attainment over one school year.",
    strengths:
      "The design suits the question, the intervention is described clearly enough to replicate, and the writing is clear.",
    m1Location: "Methods, sample size",
    m1Concern:
      "No power calculation is reported, so the reader cannot judge whether the trial could detect an effect of the size the authors expected, or what a null result on the secondary outcomes would mean.",
    m1Request:
      "State how the target of 14 schools was determined, or report the smallest effect the trial could detect.",
    m2Location: "Methods, outcome assessment",
    m2Concern:
      "The reading test was administered by teachers who knew which classes received tutoring. Unblinded assessment can bias scores towards the expected result.",
    m2Request:
      "Say who scored the test and whether they knew the allocation; if they did, discuss this as a limitation.",
    m3Location: "Discussion",
    m3Concern:
      "The effect size is not compared with those of other peer-tutoring programmes, so its practical importance is hard to judge.",
    m3Request: "Compare the effect size with earlier studies.",
    minor:
      "Table 2: give the units for the attendance variable.\np. 8, l. 140: “significant” is used for both statistical and practical importance; please separate them.\nAbstract: state the number of schools and pupils.",
    scope:
      "I did not assess the qualitative interviews described in the supplement.",
    rationale:
      "The conclusions may well be supported, but the missing power calculation and the unblinded assessment (major comments 1 and 2) need to be addressed before the main result can be relied on.",
    confidential:
      "I have no concerns about research integrity. The ethics approval statement is complete.",
  },
} as const;

const EMPTY: PeerReviewReportInput = {
  title: "",
  journal: "",
  manuscriptId: "",
  summary: "",
  strengths: "",
  major: [emptyMajor("m1"), emptyMajor("m2")],
  minor: "",
  scope: "",
  recommendation: "",
  rationale: "",
  confidential: "",
  noConflict: false,
  keptConfidential: false,
  helpers: "",
};

const RECOMMENDATIONS: readonly Recommendation[] = [
  "",
  "accept",
  "minor",
  "major",
  "reject",
];
const PRIORITIES: readonly Priority[] = ["", "essential", "strengthen"];

type TextKey =
  | "title"
  | "journal"
  | "manuscriptId"
  | "summary"
  | "strengths"
  | "minor"
  | "scope"
  | "rationale"
  | "confidential"
  | "helpers";
type MajorKey = Exclude<keyof MajorComment, "id">;

export default function PeerReviewReportGeneratorWidget() {
  const strings = useToolUi(PEER_REVIEW_REPORT_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const [input, setInput] = useState<PeerReviewReportInput>(EMPTY);
  const [nextId, setNextId] = useState(3);
  const labels = useMemo(
    () => ({
      ...strings,
      priorities: strings.priorityLines,
    }),
    [strings]
  );
  const doc = useMemo(
    () => buildPeerReviewReport(input, labels),
    [input, labels]
  );
  const documentLabels = useMemo(() => strings.document, [strings]);
  const counts = reportCounts(input);
  const checkText = strings.checkText as Record<string, string>;
  const checks = reportChecks(input).map((check) => ({
    key: check.id,
    state: check.state,
    text: t(checkText[`${check.id}_${check.variant}`] ?? "", {
      items: (check.items ?? []).join(", "),
    }),
  }));

  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));
  const updateMajor = (id: string, key: MajorKey, value: string) =>
    setInput((previous) => ({
      ...previous,
      major: previous.major.map((comment) =>
        comment.id === id ? { ...comment, [key]: value } : comment
      ),
    }));
  const addMajor = () => {
    setInput((previous) => ({
      ...previous,
      major: [...previous.major, emptyMajor(`m${nextId}`)],
    }));
    setNextId((value) => value + 1);
  };
  const removeMajor = (id: string) =>
    setInput((previous) => ({
      ...previous,
      major: previous.major.filter((comment) => comment.id !== id),
    }));
  const loadExample = () => {
    const e = strings.example;
    setInput({
      ...EMPTY,
      title: e.title,
      journal: e.journal,
      manuscriptId: e.manuscriptId,
      summary: e.summary,
      strengths: e.strengths,
      major: [
        {
          id: "m1",
          location: e.m1Location,
          concern: e.m1Concern,
          request: e.m1Request,
          priority: "essential",
        },
        {
          id: "m2",
          location: e.m2Location,
          concern: e.m2Concern,
          request: e.m2Request,
          priority: "essential",
        },
        {
          id: "m3",
          location: e.m3Location,
          concern: e.m3Concern,
          request: e.m3Request,
          priority: "strengthen",
        },
      ],
      minor: e.minor,
      scope: e.scope,
      recommendation: "major",
      rationale: e.rationale,
      confidential: e.confidential,
      noConflict: true,
      keptConfidential: true,
    });
    setNextId(4);
  };
  const clear = () => {
    setInput(EMPTY);
    setNextId(3);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={loadExample}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button className={BUTTON_SECONDARY} onClick={clear} type="button">
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.manuscriptHeading}>
        <TextField
          id="pr-title"
          label={strings.titleLabel}
          onChange={update("title")}
          placeholder={strings.titlePlaceholder}
          value={input.title}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="pr-journal"
            label={strings.journalLabel}
            onChange={update("journal")}
            placeholder={strings.journalPlaceholder}
            value={input.journal}
          />
          <TextField
            id="pr-id"
            label={strings.idLabel}
            onChange={update("manuscriptId")}
            placeholder={strings.idPlaceholder}
            value={input.manuscriptId}
          />
        </div>
      </Panel>

      <Panel title={strings.summaryHeading}>
        <TextAreaField
          hint={strings.summaryHint}
          id="pr-summary"
          label={strings.summaryLabel}
          onChange={update("summary")}
          placeholder={strings.summaryPlaceholder}
          prose
          rows={4}
          value={input.summary}
        />
        <TextAreaField
          id="pr-strengths"
          label={strings.strengthsLabel}
          onChange={update("strengths")}
          placeholder={strings.strengthsPlaceholder}
          prose
          rows={3}
          value={input.strengths}
        />
      </Panel>

      <Panel title={strings.majorHeading}>
        <p className={HINT}>{strings.majorNote}</p>
        {input.major.map((comment, index) => (
          <fieldset
            className="space-y-3 rounded-lg border border-border p-4"
            key={comment.id}
          >
            <legend className="px-1 font-medium text-foreground text-sm">
              {t(strings.majorCardLabel, { n: index + 1 })}
            </legend>
            <TextField
              id={`pr-${comment.id}-where`}
              label={strings.locationLabel}
              onChange={(value) => updateMajor(comment.id, "location", value)}
              placeholder={strings.locationPlaceholder}
              value={comment.location}
            />
            <TextAreaField
              id={`pr-${comment.id}-concern`}
              label={strings.concernLabel}
              onChange={(value) => updateMajor(comment.id, "concern", value)}
              placeholder={strings.concernPlaceholder}
              prose
              rows={3}
              value={comment.concern}
            />
            <TextAreaField
              id={`pr-${comment.id}-request`}
              label={strings.requestLabel}
              onChange={(value) => updateMajor(comment.id, "request", value)}
              placeholder={strings.requestPlaceholder}
              prose
              rows={2}
              value={comment.request}
            />
            <SelectField
              id={`pr-${comment.id}-priority`}
              label={strings.priorityLabel}
              onChange={(value) => updateMajor(comment.id, "priority", value)}
              options={PRIORITIES.map((value) => ({
                value,
                label: strings.priorities[value],
              }))}
              value={comment.priority}
            />
            <button
              className={BUTTON_SMALL}
              onClick={() => removeMajor(comment.id)}
              type="button"
            >
              {t(strings.removeMajor, { n: index + 1 })}
            </button>
          </fieldset>
        ))}
        <button
          className={BUTTON_SECONDARY}
          disabled={input.major.length >= MAX_MAJOR}
          onClick={addMajor}
          type="button"
        >
          {strings.addMajor}
        </button>
      </Panel>

      <Panel title={strings.minorHeading}>
        <TextAreaField
          id="pr-minor"
          label={strings.minorLabel}
          onChange={update("minor")}
          placeholder={strings.minorPlaceholder}
          prose
          rows={4}
          value={input.minor}
        />
        <TextAreaField
          id="pr-scope"
          label={strings.scopeLabel}
          onChange={update("scope")}
          placeholder={strings.scopePlaceholder}
          prose
          rows={2}
          value={input.scope}
        />
        <SelectField
          id="pr-recommendation"
          label={strings.recommendationLabel}
          onChange={(recommendation) =>
            setInput((previous) => ({ ...previous, recommendation }))
          }
          options={RECOMMENDATIONS.map((value) => ({
            value,
            label: strings.recommendationOptions[value],
          }))}
          value={input.recommendation}
        />
        <TextAreaField
          id="pr-rationale"
          label={strings.rationaleLabel}
          onChange={update("rationale")}
          placeholder={strings.rationalePlaceholder}
          prose
          rows={2}
          value={input.rationale}
        />
      </Panel>

      <Panel title={strings.editorHeading}>
        <TextAreaField
          hint={strings.confidentialHint}
          id="pr-confidential"
          label={strings.confidentialLabel}
          onChange={update("confidential")}
          placeholder={strings.confidentialPlaceholder}
          prose
          rows={3}
          value={input.confidential}
        />
        <div className="space-y-2">
          <Checkbox
            checked={input.noConflict}
            label={strings.noConflictLabel}
            onChange={(noConflict) =>
              setInput((previous) => ({ ...previous, noConflict }))
            }
          />
          <Checkbox
            checked={input.keptConfidential}
            label={strings.keptConfidentialLabel}
            onChange={(keptConfidential) =>
              setInput((previous) => ({ ...previous, keptConfidential }))
            }
          />
        </div>
        <TextAreaField
          id="pr-helpers"
          label={strings.helpersLabel}
          onChange={update("helpers")}
          placeholder={strings.helpersPlaceholder}
          prose
          rows={2}
          value={input.helpers}
        />
      </Panel>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        <StatGrid>
          <StatCard
            emphasis
            label={strings.statWords}
            value={fmt(counts.words)}
          />
          <StatCard label={strings.statMajor} value={fmt(counts.major)} />
          <StatCard label={strings.statMinor} value={fmt(counts.minor)} />
          <StatCard
            label={strings.statEssential}
            value={fmt(counts.essential)}
          />
        </StatGrid>
        <CheckList
          items={checks}
          label={strings.checksLabel}
          stateLabels={strings.checkStates}
        />
      </section>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="pr"
        labels={documentLabels}
      />
    </div>
  );
}
